import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import CommunicationDetails from "./CommunicationDetails";
import UcafForm from "./ucaf/UcafForm";
import claimsGQL from "../../../gql/claims";
import Encounter from "./response/Encounter";
import AccidentInfo from "./response/AccidentInfo";
import VisionPrescription from "./response/VisionPrescription";
import Validations from "./response/Details/Validations";
import "../styles/pre-auth.css";
import AppModal from "../../../components/AppModal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DcafForm from "./ucaf/DcafForm";
import ItemRejectReason from "./ItemRejectReason";
import { TbEditCircle } from "react-icons/tb";
import ClaimResponseDetailComponent from "../../PreAuth/components/response/ClaimResponseDetailComponent";
import ClaimDetailItemsTable from "../../PreAuth/components/response/ClaimDetailItemsTable";
import DiagnosisTable from "../../PreAuth/components/response/DiagnosisTable";
import CareTeamsTable from "../../PreAuth/components/response/CareTeamsTable";
import SupportingInfoTable from "../../PreAuth/components/response/SupportingInfoTable";
import PreAuthSkeleton from "../../../common/PreAuthSkeleton";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import { useTranslation } from "react-i18next";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ClaimsDetails = ({}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const currentItem = location.state?.currentItem
    ? location.state.currentItem
    : null;
  const fromSubmitClaims = location.state?.fromSubmitClaims || false;
  const [claimRef, setClaimRef] = useState(
    location.state?.claimRef ? location.state.claimRef : null,
  );
  const [fromPayment, setFromPayment] = useState(
    location.state?.fromPayment ? location.state.fromPayment : false,
  );

  const { id } = useParams();

  const [showUCAF, setShowUCAF] = useState(false);
  const [showDCAF, setShowDCAF] = useState(false);
  const [currentTab, setCurrentTab] = useState("details");
  const [actualDetailsData, setActualDetailsData] = useState(null);
  const [actualLoadingDetails, setActualLoadingDetails] = useState(false);
  const [itemsSearchTerm, setItemsSearchTerm] = useState("");

  const handleTabClick = (tabName) => setCurrentTab(tabName);

  const {
    loading: loadingDetails,
    error: detailsError,
    data: detailsData,
    refetch: refetchDetails,
  } = useQuery(claimsGQL.GET_CLAIMS_TRAN_DETAILS, {
    variables: {
      transactionId: id ? id : currentItem?.transaction_id || claimRef,
    },
    onCompleted: (data) => {
      setActualDetailsData(data);
      setActualLoadingDetails(false);
    },
    onError: () => setActualLoadingDetails(false),
  });

  const tabs = [
    { label: t("Details"), value: "details" },
    { label: t("Communications"), value: "communications" },
    { label: t("Encounter"), value: "encounter" },
    { label: t("Validations"), value: "validations" },
    { label: t("Vision Prescription"), value: "visionPrescription" },
    { label: t("Accident"), value: "accident" },
    { label: t("Reject Reason"), value: "rejectReason" },
  ];

  const tabToActive = location?.state?.tabToActive;
  useEffect(() => {
    setCurrentTab(tabToActive || "details");
  }, []);

  const claimData =
    actualDetailsData?.getClaimTransaction?.data ||
    actualDetailsData?.getClaimTransactionWithRef?.data;

  return (
    <>
      {actualLoadingDetails || loadingDetails ? (
        <PreAuthSkeleton />
      ) : (
        <div className="pre_auth_detail_page">
          <div className="flex justify-between flex-col md:flex-row gap-4 mb-2 items-center border-b-gray-300">
            <div className="flex !gap-2 items-center md:justify-center">
              <div
                className="cursor-pointer bg-[#0070C2] h-[24px] w-[24px] flex items-center justify-center rounded-full hover:bg-blue-700 transition-colors"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                    fill="white"
                  />
                </svg>
              </div>

              <div className="flex items-center gap-3">
                <Tooltip
                  placement="right"
                  title="Keep track of your Claim details"
                >
                  <span className="font-semibold text-black/70 text-[14px]">
                    <span className="text-[14px]">
                      {t("Claim")}:
                      <span className="font-medium text-[13px] ms-1">
                        {claimData?.transaction_id}
                      </span>
                    </span>
                  </span>
                </Tooltip>
                <div className="text-[13px] font-medium rounded-md min-w-[100px]">
                  <span className="font-semibold text-black/70 text-[14px]">
                    {t("Request Bundle ID")}:
                  </span>
                  <span className="!text-primary !font-[600] ml-1 text-[13px]">
                    {claimData?.request_bundle_id}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-1.5 flex-col md:flex-row justify-end">
              <AppButton
                label={t("Edit Claim")}
                startIcon={<TbEditCircle />}
                onClick={() =>
                  navigate(`/claims/edit/form/${claimData?.transaction_id}`)
                }
              />
              <AppButton
                variant="contained"
                className="!px-5"
                onClick={() => {
                  claimData?.type === "oral"
                    ? setShowDCAF(true)
                    : setShowUCAF(true);
                }}
                startIcon={<AddIcon />}
                label={claimData?.type === "oral" ? t("DCAF") : t("UCAF")}
              />
              {claimData?.status === "rejected" && (
                <AppButton
                  variant="outlined"
                  onClick={() =>
                    navigate(
                      `/claims/edit/form/${claimData?.transaction_id}/follow-up`,
                    )
                  }
                  startIcon={<AddIcon />}
                  label={t("Follow Up")}
                  className="!border-primary !text-primary hover:!bg-primary/10"
                />
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="my-2">
            <ul className="flex flex-wrap text-center text-gray-500 bg-gray-100 rounded-lg p-1 shadow-inner gap-0.5">
              {tabs.map((tab) => (
                <li key={tab.value} className="flex-1">
                  <a
                    className={`flex justify-center py-0.5 cursor-pointer text-[13px] rounded-lg transition-colors hover:text-[#0070C2] ${
                      currentTab === tab.value ? "bg-[#0070C2] !text-white" : ""
                    }`}
                    onClick={() => handleTabClick(tab.value)}
                  >
                    {tab.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Tab Content */}
          <>
            {currentTab === "details" && (
              <>
                <div>
                  <ClaimResponseDetailComponent item={claimData} />
                </div>

                <Box className="my-2">
                  <div className="flex items-center justify-between mb-2">
                    <Typography
                      className="flex gap-2 !font-bold !text-[18px]"
                      variant="h6"
                      sx={{ width: "50%", flexShrink: 0 }}
                    >
                      {t("Items")}
                    </Typography>
                    {fromSubmitClaims && (
                      <CustomFilterInput
                        placeholder={t("Filter items...")}
                        value={itemsSearchTerm}
                        onChange={(e) => setItemsSearchTerm(e.target.value)}
                        className="w-[240px]"
                      />
                    )}
                  </div>
                  <ClaimDetailItemsTable
                    data={actualDetailsData}
                    searchTerm={itemsSearchTerm}
                    showStatus={true}
                  />
                </Box>

                <Box className="my-2">
                  <Typography
                    className="flex gap-2 !font-bold mb-2 !text-[18px]"
                    variant="h6"
                    sx={{ width: "50%", flexShrink: 0 }}
                  >
                    {t("Diagnosis")}
                  </Typography>
                  <DiagnosisTable
                    data={
                      actualDetailsData?.getClaimTransaction ||
                      actualDetailsData?.getClaimTransactionWithRef
                    }
                  />
                </Box>

                <Box className="my-2">
                  <Typography
                    className="flex gap-2 !font-bold mb-2 !text-[18px]"
                    variant="h6"
                    sx={{ width: "50%", flexShrink: 0 }}
                  >
                    {t("Care Teams")}
                  </Typography>
                  <CareTeamsTable
                    data={
                      actualDetailsData?.getClaimTransaction ||
                      actualDetailsData?.getClaimTransactionWithRef
                    }
                  />
                </Box>

                <Box className="my-2">
                  <Typography
                    className="flex gap-2 !font-bold mb-2 !text-[18px]"
                    variant="h6"
                    sx={{ width: "50%", flexShrink: 0 }}
                  >
                    {t("Supporting Info")}
                  </Typography>
                  <SupportingInfoTable
                    data={
                      actualDetailsData?.getClaimTransaction ||
                      actualDetailsData?.getClaimTransactionWithRef
                    }
                  />
                </Box>
              </>
            )}

            {currentTab === "communications" && (
              <CommunicationDetails
                currentItem={currentItem}
                item={actualDetailsData}
              />
            )}

            {currentTab === "encounter" && (
              <Encounter item={claimData?.encounter} />
            )}

            {currentTab === "validations" && <Validations item={claimData} />}

            {currentTab === "visionPrescription" && (
              <VisionPrescription item={claimData} />
            )}

            {currentTab === "accident" && <AccidentInfo item={claimData} />}

            {currentTab === "rejectReason" && (
              <ItemRejectReason item={claimData} />
            )}
          </>
        </div>
      )}

      <UcafForm
        item={claimData}
        showUCAF={showUCAF}
        setShowUCAF={setShowUCAF}
      />
      <DcafForm
        item={claimData}
        showDCAF={showDCAF}
        setShowDCAF={setShowDCAF}
      />
    </>
  );
};

export default ClaimsDetails;
